// Layer 3 (Transformations / seed data): per-project module payloads keyed by
// project slug from project-seed.ts. Workflows, editor, dashboards, and tasks
// scenes hydrate local React state from these records.

import type { ProjectColor } from './project-colors';

export type WorkflowTrigger = 'manual' | 'schedule';
export type WorkflowStatus = 'success' | 'running' | 'failed' | 'disabled';

export type WorkflowRun = {
  id: string;
  name: string;
  trigger: WorkflowTrigger;
  /** ms epoch; null = never run */
  lastRunAt: number | null;
  status: WorkflowStatus;
  runsCount: number;
};

export type EditorDocument = {
  id: string;
  title: string;
  /** ms epoch */
  updatedAt: number;
  content: string;
};

export type DashboardPeriod = '7d' | '30d' | '90d';

export type DashboardStat = {
  id: string;
  label: string;
  value: string;
  sub: string;
};

export type DashboardChartSeries = {
  bar: number[];
  line: number[];
};

export type DashboardPeriodData = {
  stats: DashboardStat[];
  charts: {
    throughput: DashboardChartSeries;
    latency: DashboardChartSeries;
  };
};

export type TaskPriority = 'high' | 'medium' | 'low';
export type TaskColumn = 'todo' | 'in_progress' | 'done';

export type ProjectTask = {
  id: string;
  title: string;
  assigneeInitials: string;
  /** ms epoch; null = no due date */
  dueAt: number | null;
  priority: TaskPriority;
  column: TaskColumn;
};

export type ProjectModulesSeed = {
  /**
   * K19: the plant project's Workflows page lists the OptiFlow workflow
   * catalogue (docs/engines/workflows.md) instead of foundation seed rows.
   */
  workflowCatalogue?: 'optiflow';
  workflows: WorkflowRun[];
  documents: EditorDocument[];
  dashboards: Record<DashboardPeriod, DashboardPeriodData>;
  tasks: ProjectTask[];
};

const now = Date.now();
const hour = 60 * 60 * 1000;
const day = 24 * hour;

const ALPHA_DASHBOARDS: Record<DashboardPeriod, DashboardPeriodData> = {
  '7d': {
    stats: [
      { id: 'runs', label: 'Workflow runs', value: '142', sub: '+18% vs prior week' },
      { id: 'tasks', label: 'Tasks closed', value: '23', sub: '6 still in progress' },
      { id: 'active', label: 'Active workflows', value: '8', sub: '2 scheduled daily' },
      { id: 'latency', label: 'Avg latency', value: '198 ms', sub: 'P95 within SLO' },
    ],
    charts: {
      throughput: { bar: [12, 18, 9, 22, 15, 28, 19], line: [8, 14, 11, 19, 13, 24, 17] },
      latency: { bar: [240, 210, 195, 220, 180, 165, 190], line: [260, 230, 205, 215, 175, 160, 185] },
    },
  },
  '30d': {
    stats: [
      { id: 'runs', label: 'Workflow runs', value: '518', sub: '+12% vs prior month' },
      { id: 'tasks', label: 'Tasks closed', value: '84', sub: '14 carried over' },
      { id: 'active', label: 'Active workflows', value: '8', sub: '1 disabled this month' },
      { id: 'latency', label: 'Avg latency', value: '178 ms', sub: '−12 ms vs prior month' },
    ],
    charts: {
      throughput: {
        bar: [42, 38, 51, 47, 55, 49, 58, 52, 61, 57],
        line: [35, 32, 44, 41, 48, 43, 50, 46, 54, 49],
      },
      latency: {
        bar: [205, 198, 192, 188, 175, 182, 170, 168, 162, 159],
        line: [215, 208, 200, 195, 185, 190, 178, 172, 168, 165],
      },
    },
  },
  '90d': {
    stats: [
      { id: 'runs', label: 'Workflow runs', value: '1.4k', sub: 'Steady quarter-over-quarter' },
      { id: 'tasks', label: 'Tasks closed', value: '241', sub: 'Avg 2.7 days to close' },
      { id: 'active', label: 'Active workflows', value: '9', sub: 'Peak load in week 6' },
      { id: 'latency', label: 'Avg latency', value: '172 ms', sub: 'Trending down' },
    ],
    charts: {
      throughput: {
        bar: [120, 135, 128, 142, 138, 151, 147, 156, 149, 162, 158, 171],
        line: [108, 122, 118, 130, 125, 138, 132, 142, 136, 148, 144, 155],
      },
      latency: {
        bar: [225, 218, 210, 205, 198, 192, 188, 182, 176, 172, 168, 165],
        line: [235, 228, 220, 212, 205, 198, 192, 186, 180, 176, 172, 168],
      },
    },
  },
};

const BETA_DASHBOARDS: Record<DashboardPeriod, DashboardPeriodData> = {
  '7d': {
    stats: [
      { id: 'runs', label: 'Workflow runs', value: '18', sub: 'Light automation usage' },
      { id: 'tasks', label: 'Tasks closed', value: '5', sub: '2 open' },
      { id: 'active', label: 'Active workflows', value: '3', sub: 'All manual triggers' },
      { id: 'latency', label: 'Avg latency', value: '288 ms', sub: 'Higher variance' },
    ],
    charts: {
      throughput: { bar: [3, 2, 4, 1, 3, 2, 3], line: [2, 2, 3, 1, 2, 2, 2] },
      latency: { bar: [310, 295, 280, 305, 290, 275, 285], line: [320, 305, 290, 315, 300, 285, 295] },
    },
  },
  '30d': {
    stats: [
      { id: 'runs', label: 'Workflow runs', value: '64', sub: 'Mostly manual triggers' },
      { id: 'tasks', label: 'Tasks closed', value: '14', sub: '4 open' },
      { id: 'active', label: 'Active workflows', value: '3', sub: 'No schedules yet' },
      { id: 'latency', label: 'Avg latency', value: '282 ms', sub: 'Stable pilot range' },
    ],
    charts: {
      throughput: { bar: [8, 6, 9, 7, 10, 8, 9, 7, 6, 8], line: [6, 5, 7, 6, 8, 7, 7, 6, 5, 7] },
      latency: { bar: [300, 290, 285, 295, 280, 275, 270, 278, 272, 268], line: [310, 300, 295, 305, 290, 285, 280, 288, 282, 278] },
    },
  },
  '90d': {
    stats: [
      { id: 'runs', label: 'Workflow runs', value: '186', sub: 'Pilot phase' },
      { id: 'tasks', label: 'Tasks closed', value: '38', sub: 'Small team throughput' },
      { id: 'active', label: 'Active workflows', value: '3', sub: 'Manual-only cadence' },
      { id: 'latency', label: 'Avg latency', value: '295 ms', sub: 'Within pilot SLO' },
    ],
    charts: {
      throughput: { bar: [22, 24, 20, 26, 23, 25, 21, 24, 22, 27, 25, 28], line: [18, 20, 17, 22, 19, 21, 18, 20, 19, 23, 21, 24] },
      latency: { bar: [320, 315, 308, 302, 298, 292, 288, 285, 280, 276, 272, 268], line: [330, 325, 318, 312, 308, 302, 298, 295, 290, 286, 282, 278] },
    },
  },
};

const GAMMA_DASHBOARDS: Record<DashboardPeriod, DashboardPeriodData> = {
  '7d': {
    stats: [
      { id: 'runs', label: 'Workflow runs', value: '9', sub: 'Shared project baseline' },
      { id: 'tasks', label: 'Tasks closed', value: '3', sub: '1 in review' },
      { id: 'active', label: 'Active workflows', value: '2', sub: 'Field study triggers' },
      { id: 'latency', label: 'Avg latency', value: '322 ms', sub: 'Remote field variance' },
    ],
    charts: {
      throughput: { bar: [1, 2, 1, 0, 2, 1, 2], line: [1, 1, 1, 0, 1, 1, 1] },
      latency: { bar: [340, 330, 325, 335, 320, 315, 318], line: [350, 340, 335, 345, 330, 325, 328] },
    },
  },
  '30d': {
    stats: [
      { id: 'runs', label: 'Workflow runs', value: '31', sub: 'Field study cadence' },
      { id: 'tasks', label: 'Tasks closed', value: '11', sub: '3 open' },
      { id: 'active', label: 'Active workflows', value: '2', sub: 'Shared ownership' },
      { id: 'latency', label: 'Avg latency', value: '308 ms', sub: 'Seasonal baseline' },
    ],
    charts: {
      throughput: { bar: [4, 3, 5, 2, 4, 3, 4, 3, 2, 3], line: [3, 2, 4, 2, 3, 2, 3, 2, 2, 2] },
      latency: { bar: [335, 328, 322, 318, 312, 308, 305, 300, 298, 295], line: [345, 338, 332, 328, 322, 318, 315, 310, 308, 305] },
    },
  },
  '90d': {
    stats: [
      { id: 'runs', label: 'Workflow runs', value: '92', sub: 'Seasonal field work' },
      { id: 'tasks', label: 'Tasks closed', value: '29', sub: 'Shared ownership' },
      { id: 'active', label: 'Active workflows', value: '2', sub: 'Low automation count' },
      { id: 'latency', label: 'Avg latency', value: '318 ms', sub: 'Field network overhead' },
    ],
    charts: {
      throughput: { bar: [10, 12, 9, 11, 10, 13, 11, 12, 10, 14, 12, 13], line: [8, 10, 7, 9, 8, 11, 9, 10, 8, 12, 10, 11] },
      latency: { bar: [350, 342, 335, 328, 322, 318, 312, 308, 302, 298, 295, 290], line: [360, 352, 345, 338, 332, 328, 322, 318, 312, 308, 305, 300] },
    },
  },
};

const DELTA_DASHBOARDS: Record<DashboardPeriod, DashboardPeriodData> = {
  '7d': {
    stats: [
      { id: 'runs', label: 'Workflow runs', value: '0', sub: 'No activity yet' },
      { id: 'tasks', label: 'Tasks closed', value: '0', sub: 'Board is empty' },
      { id: 'active', label: 'Active workflows', value: '0', sub: 'None configured' },
      { id: 'latency', label: 'Avg latency', value: '—', sub: 'No samples' },
    ],
    charts: {
      throughput: { bar: [0, 0, 0, 0, 0, 0, 0], line: [0, 0, 0, 0, 0, 0, 0] },
      latency: { bar: [0, 0, 0, 0, 0, 0, 0], line: [0, 0, 0, 0, 0, 0, 0] },
    },
  },
  '30d': {
    stats: [
      { id: 'runs', label: 'Workflow runs', value: '0', sub: 'No activity yet' },
      { id: 'tasks', label: 'Tasks closed', value: '0', sub: 'Board is empty' },
      { id: 'active', label: 'Active workflows', value: '0', sub: 'None configured' },
      { id: 'latency', label: 'Avg latency', value: '—', sub: 'No samples' },
    ],
    charts: {
      throughput: { bar: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0], line: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0] },
      latency: { bar: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0], line: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0] },
    },
  },
  '90d': {
    stats: [
      { id: 'runs', label: 'Workflow runs', value: '0', sub: 'No activity yet' },
      { id: 'tasks', label: 'Tasks closed', value: '0', sub: 'Board is empty' },
      { id: 'active', label: 'Active workflows', value: '0', sub: 'None configured' },
      { id: 'latency', label: 'Avg latency', value: '—', sub: 'No samples' },
    ],
    charts: {
      throughput: { bar: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], line: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0] },
      latency: { bar: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], line: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0] },
    },
  },
};

export const PRIORITY_COLOR: Record<TaskPriority, ProjectColor> = {
  high: 'crimson',
  medium: 'ochre',
  low: 'slate',
};

export const PROJECT_MODULES_SEED: Record<string, ProjectModulesSeed> = {
  alpha: {
    workflowCatalogue: 'optiflow',
    workflows: [],
    documents: [
      {
        id: 'doc-1',
        title: 'Project brief',
        updatedAt: now - 2 * hour,
        content:
          '# Project Alpha — brief\n\nScope the module rail demo with credible seeded surfaces across workflows, editor, dashboards, and tasks.\n\n## Goals\n- Mirror portfolio density\n- Local state only\n- Per-project variation',
      },
      {
        id: 'doc-2',
        title: 'API integration notes',
        updatedAt: now - 1 * day,
        content:
          'Connector auth flows:\n\n1. OAuth redirect for GitHub\n2. API key for internal tools\n3. Webhook signature validation on ingest',
      },
      {
        id: 'doc-3',
        title: 'Sprint retro — Aug W1',
        updatedAt: now - 3 * day,
        content:
          'Went well:\n- Portfolio table landed\n- Chat-first routing holds\n\nImprove:\n- Module surfaces still thin\n- Need empty states for delta',
      },
      {
        id: 'doc-4',
        title: 'Research log',
        updatedAt: now - 5 * day,
        content:
          'Field notes from stakeholder interviews. Key theme: users want project-scoped automations without leaving chat context.',
      },
    ],
    dashboards: ALPHA_DASHBOARDS,
    tasks: [
      {
        id: 'task-1',
        title: 'Wire workflow row menu actions',
        assigneeInitials: 'ML',
        dueAt: now + 2 * day,
        priority: 'high',
        column: 'todo',
      },
      {
        id: 'task-2',
        title: 'Draft editor autosave copy',
        assigneeInitials: 'AK',
        dueAt: now + 4 * day,
        priority: 'medium',
        column: 'todo',
      },
      {
        id: 'task-3',
        title: 'Chart SVG polish',
        assigneeInitials: 'JR',
        dueAt: now + 1 * day,
        priority: 'high',
        column: 'in_progress',
      },
      {
        id: 'task-4',
        title: 'Task board list view',
        assigneeInitials: 'ML',
        dueAt: now + 3 * day,
        priority: 'medium',
        column: 'in_progress',
      },
      {
        id: 'task-5',
        title: 'Seed delta empty states',
        assigneeInitials: 'AK',
        dueAt: null,
        priority: 'low',
        column: 'in_progress',
      },
      {
        id: 'task-6',
        title: 'Portfolio metric tiles',
        assigneeInitials: 'JR',
        dueAt: now - 1 * day,
        priority: 'medium',
        column: 'done',
      },
      {
        id: 'task-7',
        title: 'Project home chats section',
        assigneeInitials: 'ML',
        dueAt: now - 2 * day,
        priority: 'low',
        column: 'done',
      },
      {
        id: 'task-8',
        title: 'Module rail nav labels',
        assigneeInitials: 'AK',
        dueAt: now - 4 * day,
        priority: 'low',
        column: 'done',
      },
    ],
  },
  beta: {
    workflows: [
      {
        id: 'wf-b1',
        name: 'Import staging data',
        trigger: 'manual',
        lastRunAt: now - 3 * day,
        status: 'success',
        runsCount: 11,
      },
      {
        id: 'wf-b2',
        name: 'Friday summary email',
        trigger: 'schedule',
        lastRunAt: now - 5 * day,
        status: 'success',
        runsCount: 4,
      },
      {
        id: 'wf-b3',
        name: 'Archive stale threads',
        trigger: 'schedule',
        lastRunAt: now - 12 * day,
        status: 'disabled',
        runsCount: 2,
      },
    ],
    documents: [
      {
        id: 'doc-b1',
        title: 'Beta rollout checklist',
        updatedAt: now - 2 * day,
        content: 'Pre-launch checks for the second demo project.',
      },
      {
        id: 'doc-b2',
        title: 'Connector smoke tests',
        updatedAt: now - 6 * day,
        content: 'Verify GitHub + Drive connectors in project scope.',
      },
      {
        id: 'doc-b3',
        title: 'Meeting notes — kickoff',
        updatedAt: now - 10 * day,
        content: 'Agreed on lighter seed data for beta and gamma.',
      },
    ],
    dashboards: BETA_DASHBOARDS,
    tasks: [
      {
        id: 'task-b1',
        title: 'Validate project switcher',
        assigneeInitials: 'ML',
        dueAt: now + 5 * day,
        priority: 'medium',
        column: 'todo',
      },
      {
        id: 'task-b2',
        title: 'Smoke test workflows table',
        assigneeInitials: 'AK',
        dueAt: now + 2 * day,
        priority: 'low',
        column: 'in_progress',
      },
      {
        id: 'task-b3',
        title: 'Initial beta seed',
        assigneeInitials: 'JR',
        dueAt: now - 3 * day,
        priority: 'low',
        column: 'done',
      },
    ],
  },
  gamma: {
    workflows: [
      {
        id: 'wf-g1',
        name: 'Field note indexer',
        trigger: 'manual',
        lastRunAt: now - 26 * hour,
        status: 'success',
        runsCount: 6,
      },
      {
        id: 'wf-g2',
        name: 'Shared digest',
        trigger: 'schedule',
        lastRunAt: now - 4 * day,
        status: 'failed',
        runsCount: 3,
      },
    ],
    documents: [
      {
        id: 'doc-g1',
        title: 'Field study protocol',
        updatedAt: now - 26 * hour,
        content: 'Observation guide for shared demo participants.',
      },
      {
        id: 'doc-g2',
        title: 'Consent template',
        updatedAt: now - 8 * day,
        content: 'Standard consent language for mock interviews.',
      },
    ],
    dashboards: GAMMA_DASHBOARDS,
    tasks: [
      {
        id: 'task-g1',
        title: 'Recruit participant C',
        assigneeInitials: 'AK',
        dueAt: now + 6 * day,
        priority: 'high',
        column: 'todo',
      },
      {
        id: 'task-g2',
        title: 'Transcribe session 2',
        assigneeInitials: 'ML',
        dueAt: now + 1 * day,
        priority: 'medium',
        column: 'in_progress',
      },
      {
        id: 'task-g3',
        title: 'Shared pill on portfolio',
        assigneeInitials: 'JR',
        dueAt: now - 5 * day,
        priority: 'low',
        column: 'done',
      },
    ],
  },
  delta: {
    workflows: [],
    documents: [],
    dashboards: DELTA_DASHBOARDS,
    tasks: [],
  },
};

const EMPTY_MODULES_SEED: ProjectModulesSeed = {
  workflows: [],
  documents: [],
  dashboards: DELTA_DASHBOARDS,
  tasks: [],
};

export function projectModulesSeed(slug: string | undefined): ProjectModulesSeed {
  if (!slug) {
    return EMPTY_MODULES_SEED;
  }
  return PROJECT_MODULES_SEED[slug] ?? EMPTY_MODULES_SEED;
}
